/**
 * Outbounds.jsx — Aba "Outbounds" do DetailPanel.
 *
 * Lista de saídas (envios) vinculadas ao SKU. Fetch lazy via Context
 * (outboundsState) — só dispara quando esta tab vira ativa.
 */

import { useDetailPanel } from '../index.jsx';
import { FormatDate, OUTBOUND_STATUS_LABEL, Pager, StatusMsg } from '../internals.jsx';

/**
 * Link "+ Criar outbound" → `/wms/create`. `<a target="_blank">` (não button +
 * window.open) pra ganhar MOUSE3 / Ctrl+click do browser → "abrir em nova aba"
 * gratuitamente, sem código adicional.
 */
function CreateOutboundLink({ T }) {
    return (
        <a
            href="/wms/create"
            target="_blank"
            rel="noopener noreferrer"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 500,
                color: T.accent,
                background: 'transparent',
                border: `1px solid ${T.accent}`,
                borderRadius: 4,
                textDecoration: 'none',
                marginBottom: 10,
            }}
            data-faid="Create_Outbound_Link_DetailPanel"
        >
            + Criar outbound
        </a>
    );
}

export function Outbounds() {
    const { T, tab, outboundsState: state, outPage, outPageSize, setOutPage, setOutPageSize } = useDetailPanel();
    if (tab !== 'outbounds') return null;

    if (state.loading) {
        return (
            <div>
                <CreateOutboundLink T={T} />
                <StatusMsg T={T}>Carregando…</StatusMsg>
            </div>
        );
    }
    if (state.error) {
        return (
            <div>
                <CreateOutboundLink T={T} />
                <StatusMsg kind="error" T={T}>
                    {state.error}
                </StatusMsg>
            </div>
        );
    }
    const rows = Array.isArray(state.data?.results) ? state.data.results : [];
    const total = Number(state.data?.paging?.total ?? rows.length);
    if (rows.length === 0) {
        return (
            <div>
                <CreateOutboundLink T={T} />
                <StatusMsg T={T}>Nenhum outbound vinculado a este SKU.</StatusMsg>
            </div>
        );
    }
    return (
        <div>
            <CreateOutboundLink T={T} />
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, overflow: 'hidden' }}>
                {rows.map((r, i) => {
                    const status = OUTBOUND_STATUS_LABEL[r.status] || r.status || '—';
                    return (
                        <div
                            key={r.id || r.outbound_id || `${r.freight_code || 'out'}-${i}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 110px 80px 130px',
                                padding: '8px 12px',
                                fontSize: 12,
                                borderBottom: i < rows.length - 1 ? `1px solid ${T.line}` : 'none',
                                background: i % 2 === 0 ? T.bg : T.bgSoft,
                                gap: 8,
                                alignItems: 'center',
                            }}
                        >
                            <span
                                style={{
                                    fontFamily: T.mono,
                                    color: T.ink,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {r.outbound_id || r.id || '—'}
                            </span>
                            <span style={{ color: T.inkMid }}>{status}</span>
                            <span style={{ fontFamily: T.mono, color: T.ink, textAlign: 'right' }}>
                                {Number(r.units || 0)} un
                            </span>
                            <span style={{ color: T.muted, fontSize: 11 }}>
                                {FormatDate(r.requested_at || r.created_at)}
                            </span>
                        </div>
                    );
                })}
            </div>
            <Pager
                total={total}
                page={outPage}
                pageSize={outPageSize}
                onPageChange={setOutPage}
                onPageSizeChange={setOutPageSize}
                T={T}
            />
        </div>
    );
}
